import React, { useRef } from 'react';
import styled, { css, Keyframes } from 'styled-components';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { tracker } from 'src/utils/analytics';
import { useConciergeNavigate } from './use-concierge-navigate';
import {
  baseTravel,
  colorPulse,
  glowLayers,
  GREEN_TRANSPARENT,
  HIGHLIGHT_GOLD,
  LENS_GOLD,
  LENS_GREEN,
  LENS_LENGTH,
  lensLayers,
  ringMaskCss,
  spin,
  SVG_PAD,
  useButtonMetrics,
  useNeedsDarkening,
} from './concierge-button-effects';

type ConciergeNavButtonProps = {
  cName: string | null;
  onClick?: () => void;
  className?: string;
};

export const ConciergeNavButton = ({ cName, onClick, className }: ConciergeNavButtonProps): JSX.Element | null => {
  const navigateToConcierge = useConciergeNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const metrics = useButtonMetrics(buttonRef);
  const needsDarkening = useNeedsDarkening(buttonRef);

  if (!cName) return null;

  const handleClick = (): void => {
    tracker.conciergeEntryPointClicked({ location: 'nav' });
    (onClick ?? navigateToConcierge)();
  };

  const rectProps = metrics
    ? {
        x: SVG_PAD,
        y: SVG_PAD,
        width: metrics.w,
        height: metrics.h,
        rx: Math.min(metrics.radius, metrics.h / 2),
        pathLength: 100,
      }
    : null;

  return (
    <NavButton
      ref={buttonRef}
      type='button'
      className={className}
      onClick={handleClick}
      data-testid='concierge-nav-button'
    >
      <HoverGlow aria-hidden />
      <Face $darken={needsDarkening}>
        <IconWrap aria-hidden>
          <BlackSparkleIcon width={16} height={16} fill='currentColor' />
        </IconWrap>
        AI Concierge
      </Face>
      {metrics && rectProps && (
        <StrokeSvg aria-hidden viewBox={`0 0 ${metrics.w + SVG_PAD * 2} ${metrics.h + SVG_PAD * 2}`}>
          <BaseRing {...rectProps} />
          <GlowGroup>
            {/* static module-level arrays — index keys are safe */}
            {glowLayers.map((layer, i) => (
              <TaperedStroke
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                {...rectProps}
                $alpha={layer.alpha}
                $color={HIGHLIGHT_GOLD}
                $length={layer.length}
                $travel={layer.travel}
                $width={layer.width}
              />
            ))}
          </GlowGroup>
          {/* static module-level arrays — index keys are safe */}
          {lensLayers.map((layer, i) => (
            <TaperedStroke
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...rectProps}
              $alpha={layer.alpha}
              $color={LENS_GOLD}
              $length={layer.length}
              $pulse
              $travel={layer.travel}
              $width={layer.width}
            />
          ))}
          <HoverRing {...rectProps} />
        </StrokeSvg>
      )}
      <HoverShadeBand aria-hidden>
        <HoverShadeArc />
      </HoverShadeBand>
    </NavButton>
  );
};

const NavButton = styled.button`
  align-self: center;
  background: none;
  border: none;
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  cursor: pointer;
  flex-shrink: 0;
  outline: none;
  padding: 0;
  position: relative;

  &:focus-visible {
    outline: 2px solid #0075e0;
    outline-offset: 3px;
  }
`;

const HoverGlow = styled.span`
  background: radial-gradient(closest-side, var(--dcm-color-primary), transparent);
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  filter: blur(12px);
  inset: -10px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 0.85;
  }
`;

const Face = styled.span<{ $darken: boolean }>`
  align-items: center;
  background: ${({ $darken }) =>
    $darken
      ? 'linear-gradient(rgba(0, 0, 0, 0.30), rgba(0, 0, 0, 0.30)), var(--dcm-color-primary)'
      : 'var(--dcm-color-primary)'};
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  color: var(--dcm-cart-btn-text-active);
  display: flex;
  font-size: var(--dcm-font-size-md);
  font-weight: var(--dcm-cart-btn-font-weight);
  gap: 7px;
  padding: 8px 16px;
  position: relative;
  white-space: nowrap;
`;

const IconWrap = styled.span`
  align-items: center;
  display: flex;
`;

const StrokeSvg = styled.svg`
  height: calc(100% + ${SVG_PAD * 2}px);
  left: -${SVG_PAD}px;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: -${SVG_PAD}px;
  width: calc(100% + ${SVG_PAD * 2}px);
`;

const BaseRing = styled.rect`
  fill: none;
  stroke: rgba(255, 255, 255, 0.25);
  stroke-width: 1;
`;

const TaperedStroke = styled.rect<{
  $alpha: number;
  $color: string;
  $length: number;
  $pulse?: boolean;
  $travel: Keyframes;
  $width: number;
}>`
  fill: none;
  stroke: ${({ $color }) => $color};
  stroke-dasharray: ${({ $length }) => `${$length} ${100 - $length}`};
  stroke-opacity: ${({ $alpha }) => $alpha};
  stroke-width: ${({ $width }) => $width};
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${({ $pulse, $travel }) =>
      $pulse
        ? css`
            ${$travel} 5s linear infinite,
            ${colorPulse} 6s ease-in-out infinite
          `
        : css`
            ${$travel} 5s linear infinite
          `};
  }

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    animation-play-state: paused;
    opacity: 0;
  }
`;

const GlowGroup = styled.g`
  filter: blur(4px);
  opacity: 0.4;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 0;
  }
`;

const HoverRing = styled.rect`
  animation: ${baseTravel} 5s linear infinite;
  fill: none;
  opacity: 0;
  stroke: ${HIGHLIGHT_GOLD};
  stroke-dasharray: ${LENS_LENGTH} ${100 - LENS_LENGTH};
  stroke-linecap: round;
  stroke-width: 1.5;
  transition: stroke-dasharray 0.5s ease, stroke-width 0.5s ease, opacity 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    animation-play-state: paused;
    opacity: 1;
    stroke-dasharray: 101 0;
    stroke-width: 1;
  }
`;

const HoverShadeBand = styled.span`
  border-radius: calc(var(--dct--customized-radius-buttons, 9999px) + 1px);
  inset: -1px;
  ${ringMaskCss}
  opacity: 0;
  overflow: hidden;
  padding: 2px;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 1;
    transition: opacity 0.4s ease 0.5s;
  }
`;

const HoverShadeArc = styled.span`
  background: conic-gradient(
    ${GREEN_TRANSPARENT} 250deg,
    ${LENS_GREEN} 296deg,
    ${LENS_GREEN} 312deg,
    ${GREEN_TRANSPARENT} 358deg
  );
  height: 260px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 260px;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 7s linear infinite;
  }
`;
